import { BBDTag, Texto } from '@gravitate-js/excalibrr'
import { UpdatedTag } from '@components/Freight/shared/UpdatedTag'
import { SingleProductGroupCellRenderer } from '@components/Freight/shared/LineItems/components'
import dayjs from 'dayjs'

import type { DateRendererProps, ProductGroupRendererProps, RegionRendererProps } from '../types'
import type { SurchargeCellRendererParams } from '../types'
import { formatDate } from '../utils'

/**
 * Component for rendering the region value
 */
export function RegionRenderer({ value }: RegionRendererProps) {
  return value ? <BBDTag theme2>{value}</BBDTag> : <Texto appearance='hint'>All Regions</Texto>
}

/**
 * Component for rendering the product group value
 */
export function ProductGroupRenderer({ value }: ProductGroupRendererProps) {
  return value ? <SingleProductGroupCellRenderer value={value} /> : <Texto appearance='hint'>All Product Groups</Texto>
}

/**
 * Component for rendering the surcharge type
 */
export const TypeRenderer = ({ value }: { value: string }) => {
  return <BBDTag theme2>{value || 'Percentage'}</BBDTag>
}

/**
 * Component for rendering the surcharge rate value
 */
export const SurchargeRateRenderer = ({ value, data: rowData }: SurchargeCellRendererParams) => {
  if (value === null || value === undefined) {
    return <Texto appearance='hint'>N/A</Texto>
  }

  const type = rowData?.type || 'Percentage'
  const formattedValue = type === 'Percentage' ? `${value.toFixed(3)}%` : `$${value.toFixed(5)}`

  return (
    <Texto weight='bold' align='right'>
      {formattedValue}
    </Texto>
  )
}

/**
 * Component for rendering the updated date/user information
 */
export function UpdatedTagRenderer({ data: rowData }: SurchargeCellRendererParams) {
  if (!rowData) return null

  return (
    <UpdatedTag
      updated={{
        by: rowData.updated_by || 'Unknown',
        date: dayjs.utc(rowData.updated_on).local().format(),
      }}
      title='Table Updated'
    />
  )
}

/**
 * Component for rendering date values
 */
export function DateRenderer({ value }: DateRendererProps) {
  return value ? <Texto>{formatDate(value)}</Texto> : <Texto appearance='hint'>N/A</Texto>
}

/**
 * Component for rendering effective to date
 */
export function EffectiveToRenderer({ value }: DateRendererProps) {
  return value ? <Texto>{formatDate(value)}</Texto> : <Texto appearance='hint'>Indefinite</Texto>
}

/**
 * Component for rendering next scheduled rate date
 */
export function NextRateRenderer({ value }: DateRendererProps) {
  return value ? <Texto>{formatDate(value)}</Texto> : <Texto appearance='hint'>Forever</Texto>
}
